import { CompanyId } from "@custom-types/base-types";
import { IReactChildrenOnly } from "@custom-types/types";
import { sentryCaptureError } from "@utils/sentry-utils";
import { createContext, use } from "react";

const CompanyIdContext = createContext<CompanyId | null>(null);

interface CompanyIdProviderProps extends IReactChildrenOnly {
  companyId: CompanyId;
}

/**
 * Provides a guaranteed non-null companyId to the subtree.
 * Must be rendered inside a boundary that already validated companyId exists
 * (e.g. SdbCompanyLoader).
 */
export function CompanyIdProvider({
  companyId,
  children,
}: CompanyIdProviderProps): JSX.Element {
  return <CompanyIdContext value={companyId}>{children}</CompanyIdContext>;
}

/**
 * Returns the current companyId. Guaranteed to be a non-null string.
 * Can only be used inside routes wrapped by CompanyIdProvider (i.e. inside /:companyId).
 *
 * This is the primary way to access companyId in components — prefer this over
 * `useAppParams().companyId` or `useAppSelector(selectedSdbCompanyIdSelector)`.
 */
export function useCompanyId(): CompanyId {
  const companyId = use(CompanyIdContext);
  if (!companyId) {
    const error = new Error(
      "useCompanyId() called outside <CompanyIdProvider>. " +
        "Use useAppParams() instead for components that render outside /:companyId routes."
    );
    sentryCaptureError({
      error,
      title: "useCompanyId() called outside CompanyIdProvider",
    });
    throw error;
  }
  return companyId;
}
